/**
 *
 * LoginPage
 *
 */

import React from 'react';
import { FormattedMessage } from 'lib/intl';
import { history } from 'lib/history';
import { AUTHENTICATE_PAGE } from 'lib/routes';

// api
import { login } from 'lib/api/auth';

// ui
import { Flex, TextField, Button, ErrInline } from '@domonda/ui';

// form
import {
  Form,
  FormSubmittingState,
  FormSubmitErrorState,
  FormInputField,
} from '@domonda/react-form';

// modules
import { BluDeltaLogo } from 'modules/BluDeltaLogo';

// decorate
import { decorate, Decorate } from './decorate';

interface FormValues {
  Username: string | null;
  Password: string | null;
}

export interface LoginPageProps {}

const LoginPage: React.FC<LoginPageProps & Decorate> = () => {

  return (
    <Flex container direction="column">
      <Flex item container flex={1} align="center" justify="center">
        <Flex item>
          <Form<FormValues>
            defaultValues={{
              Username: null,
              Password: null,
            }}
            onSubmit={async (input) => {
              const { Username, Password } = input;

              if (!Username || !Password) throw new Error('Missing required fields!');

              const response = await login({ Username, Password });

              if (!response.AccessToken) {
                throw new Error('Missing access token');
              }

              history.push(`${AUTHENTICATE_PAGE}?at=${response.AccessToken}`);
            }}
          >
            <FormSubmittingState>
              {(submitting) => (
                <Flex container direction="column" spacing={1} minWidth={384}>
                  <Flex item container justify="center">
                    <Flex item>
                      <BluDeltaLogo to="/" />
                    </Flex>
                  </Flex>

                  <Flex item>
                    <FormSubmitErrorState>
                      {(error, { resetSubmitError }) =>
                        error && <ErrInline error={error} onClose={resetSubmitError} />
                      }
                    </FormSubmitErrorState>
                  </Flex>

                  <Flex item>
                    <FormInputField path="Username">
                      {({ inputProps }) => (
                        <TextField
                          {...inputProps}
                          disabled={submitting}
                          label={<FormattedMessage id="AUTH_FORM_USERNAME" />}
                          autoFocus
                          required
                        />
                      )}
                    </FormInputField>
                  </Flex>

                  <Flex item>
                    <FormInputField path="Password">
                      {({ inputProps }) => (
                        <TextField
                          {...inputProps}
                          disabled={submitting}
                          label={<FormattedMessage id="AUTH_FORM_PASSWORD" />}
                          required
                          type="password"
                        />
                      )}
                    </FormInputField>
                  </Flex>

                  <Flex item container justify="flex-end">
                    <Flex item>
                      <Button
                        type="submit"
                        color="secondary"
                        disabled={submitting}
                        variant="contained"
                      >
                        {submitting ? (
                          <FormattedMessage id="LOGGING_IN" />
                        ) : (
                          <FormattedMessage id="LOGIN" />
                        )}
                      </Button>
                    </Flex>
                  </Flex>
                </Flex>
              )}
            </FormSubmittingState>
          </Form>
        </Flex>
      </Flex>
    </Flex>
  );
};

const StyledLoginPage = decorate(LoginPage);
export { StyledLoginPage as LoginPage };
